/**
 * Textes de la correspondance des lots tirés d'un dépôt (ARTO-130) : création
 * du lot, profil des sources, éditeur de règles, aperçu, choix de la galerie,
 * transformation en morceaux, bibliothèque de profils. Fichier à part, comme
 * `i18nReprise.ts` ; son bloc `correspondance` est fusionné dans le namespace
 * `console` (D15). Voix 2.2 : phrases courtes, un geste porte le même nom du
 * bouton au message de fin.
 */

export const frCorrespondance = {
  correspondance: {
    etapes: {
      aria: 'Avancement du lot',
      depose: 'Déposé',
      profile: 'Profilé',
      correspondance: 'Correspondance',
      transforme: 'Transformé',
      essai: 'Essai par morceaux',
      a_lancer: 'Morceaux à lancer',
      etat: { fait: 'Fait', courante: 'À faire maintenant', a_venir: 'À venir' },
      detailEssai: '{{faits}} sur {{total}} essayés',
      detailLancement: '{{faits}} sur {{total}} lancés',
    },
    liste: {
      depuisDepotBouton: 'Lot depuis un dépôt',
      depuisDepot: 'Depuis un dépôt, à profiler',
      morceaux_one: '{{count}} morceau · prêts : {{prets}}',
      morceaux_other: '{{count}} morceaux · prêts : {{prets}}',
    },
    nouveau: {
      titre: 'Nouveau lot depuis un dépôt',
      texte:
        'Choisissez un dépôt prêt, puis les fichiers à lire. Rien n’est écrit chez la galerie avant l’essai.',
      depot: 'Dépôt',
      depotChoix: 'Dépôt à utiliser',
      depotAucunChoix: 'Choisir un dépôt',
      depotAucun: 'Aucun dépôt prêt. Un dépôt devient utilisable quand son inventaire est fait.',
      fichiers: 'Fichiers',
      illisible: 'Format non pris en charge',
      choisis_one: '{{count}} fichier choisi sur {{max}} au plus.',
      choisis_other: '{{count}} fichiers choisis sur {{max}} au plus.',
      nature: 'Nature principale du lot',
      natureAide:
        'La nature décide du profil et des champs à remplir. Les autres natures arrivent par scission, dans l’éditeur.',
      profilage: 'Profilage',
      profilageTexte:
        'Le profilage lit les fichiers et décrit leurs colonnes : formats, remplissage, dates ambiguës. Il ne modifie rien.',
      profiler: 'Lancer le profilage',
      profilageRefuse:
        'Le lot est créé, mais le profilage est refusé : {{message}} Ouvrez le lot pour le relancer.',
      ouvrir: 'Ouvrir le lot',
      annuler: 'Annuler',
      ok: 'Lot créé, profilage lancé.',
    },
    fiche: {
      titre: '{{nature}} · lot depuis un dépôt',
      profiler: 'Lancer le profilage',
      reprofiler: 'Profiler à nouveau',
      profilerAide: 'Le profilage lit les fichiers du lot et décrit leurs colonnes.',
      reprofilerAide:
        'Profiler à nouveau relit les fichiers. Les morceaux déjà lancés l’interdisent.',
      profilageEnCours: 'Profilage en cours. La page se met à jour toute seule.',
      renonce: 'Le travail a renoncé : {{message}}',
    },
    profilCite: {
      aucun: 'Aucun profil de correspondance choisi pour ce lot.',
      utilise: 'Profil utilisé : {{nom}}',
      nomInconnu: 'profil introuvable',
      version: 'version {{version}}',
      plusRecente:
        'Une version {{version}} de ce profil existe. Ce lot garde la version {{actuelle}} tant que vous ne le changez pas.',
      passer: 'Passer à la version {{version}}',
    },
    profil: {
      titre: 'Profil des sources',
      texte:
        'Ce que le profilage a lu : le format de chaque fichier, puis le remplissage, les valeurs fréquentes et le format de chaque colonne.',
      erreur: 'Le profil des sources n’a pas pu être lu.',
      tableNommee: 'Feuille « {{nom}} » · {{count}} lignes',
      tableUnique: '{{count}} lignes',
      colonne: 'Colonne',
      remplissage: 'Remplissage',
      remplissageColonne: 'Remplissage de la colonne {{nom}}',
      frequentes: 'Valeurs fréquentes',
      format: 'Format détecté',
      aucuneValeur: 'Aucune valeur',
      autresValeurs_one: '{{count}} autre valeur',
      autresValeurs_other: '{{count}} autres valeurs',
      encodage: 'encodage {{valeur}}',
      separateur: 'séparateur « {{valeur}} »',
      illisible: 'Fichier illisible : {{message}}',
      illisiblesTitre_one: '{{count}} fichier n’a pas pu être lu.',
      illisiblesTitre_other: '{{count}} fichiers n’ont pas pu être lus.',
      ambiguesTitre_one: '{{count}} colonne de dates est ambiguë.',
      ambiguesTitre_other: '{{count}} colonnes de dates sont ambiguës.',
      ambigueLigne: '« {{colonne}} » dans {{fichier}} (par exemple {{exemple}}).',
      ambiguesRegle:
        'Le jour et le mois peuvent s’inverser. Les règles doivent déclarer le format de ces dates, sinon l’enregistrement est bloqué.',
      ambigueAide: 'Jour et mois peuvent s’inverser (03/04 : 3 avril ou 4 mars ?).',
      formats: {
        vide: 'Vide',
        date_iso: 'Date (AAAA-MM-JJ)',
        date_fr: 'Date (JJ/MM/AAAA)',
        date_us: 'Date (MM/JJ/AAAA)',
        date_ambigue: 'Date ambiguë',
        annee: 'Année',
        excel_serie: 'Date en numéro de série',
        courriel: 'Courriel',
        url: 'Adresse web',
        devise: 'Devise',
        montant: 'Montant',
        nombre: 'Nombre',
        liste: 'Liste',
        texte: 'Texte',
      },
    },
    suggestions: {
      titre: 'Profils suggérés',
      aucune: 'Aucun profil connu ne ressemble à ces en-têtes. Écrivez les règles ci-dessous.',
      score: '{{score}} %',
      detail: 'Version {{version}} · {{trouves}} colonnes retrouvées sur {{attendus}}',
      appliquer: 'Appliquer comme base',
      utilise: 'Utilisé par ce lot',
      defait: 'Changer de profil défait la transformation et annule les morceaux encore libres.',
    },
    editeur: {
      titre: 'Correspondance',
      texte:
        'Dites d’où vient chaque champ du pivot. L’aperçu se met à jour à chaque changement, sur l’échantillon du profil.',
      droitProfils: 'Écrire des règles demande le droit de gérer les profils de reprise.',
      erreurChargement: 'L’éditeur n’a pas pu charger le profil ou les champs du pivot.',
      partDe: 'Règles du profil {{nom}}, version {{version}}',
      partDeRien: 'Règles à écrire : aucun profil choisi',
      modifie: 'Modifié',
      natures: 'Natures alimentées',
      ajouterNature: 'Ajouter une nature',
      retirerNature: 'Retirer la nature {{nature}}',
      retourChamps: 'Retour aux champs',
      choisirChamp: 'Choisissez un champ dans la liste.',
      obligatoire: 'Obligatoire',
      facultatif: 'Facultatif',
      origine: 'Origine du champ',
      modes: {
        colonne: 'Une colonne',
        plusieurs: 'Plusieurs colonnes',
        chemin: 'Chemin JSON ou XML',
      },
      colonne: 'Colonne source',
      colonneNumero: 'Colonne {{rang}}',
      colonneAjoutee: 'Colonne à ajouter',
      chemin: 'Chemin dans le fichier',
      ajouterColonne: 'Ajouter une colonne',
      retirerColonne: 'Retirer la colonne {{rang}}',
      separateurColonnes: 'Séparateur',
      exemples: 'Exemples dans la source : {{valeurs}}.',
      role: 'Fichier',
      rolePrincipal: 'Source principale',
      parent: 'Lire dans l’enregistrement parent',
      parentCourt: 'parent',
      dateAmbigueTitre: 'La colonne « {{colonne}} » contient des dates ambiguës.',
      dateAmbigueTexte:
        'Choisissez le format des dates de la source : le jour et le mois peuvent s’inverser.',
      ajouterDate: 'Ajouter la lecture de date',
      transformations: 'Transformations',
      aucuneTransformation:
        'Aucune transformation : la valeur de la source est reprise telle quelle.',
      etapeNumero: 'Étape {{rang}} : {{nom}}',
      typeEtape: 'Transformation',
      monter: 'Monter l’étape {{nom}}',
      descendre: 'Descendre l’étape {{nom}}',
      monterCourt: 'Monter',
      descendreCourt: 'Descendre',
      retirerEtape: 'Retirer l’étape {{nom}}',
      retirer: 'Retirer',
      nouvelleEtape: 'Nouvelle transformation',
      ajouterEtape: 'Ajouter l’étape',
      viderChamp: 'Vider ce champ',
      separateur: 'Séparateur',
      indexScinder: 'Numéro de la partie',
      indexAide: '0 pour la première, -1 pour la dernière.',
      formatDate: 'Format des dates de la source',
      decimal: 'Séparateur décimal',
      decimalCourt: 'décimale',
      espaceCourt: 'espace',
      decimalVirgule: 'Virgule (1 200,50)',
      decimalPoint: 'Point (1,200.50)',
      devise: 'Devise',
      deviseAucune: 'Sans devise',
      dictionnaire: 'Valeurs, une par ligne',
      dictionnaireAide:
        'Écrivez « valeur de la source = valeur retenue ». Casse et accents sont ignorés.',
      valeurParDefaut: 'Valeur par défaut',
      valeur: 'Valeur',
      valeurs_one: '{{count}} valeur',
      valeurs_other: '{{count}} valeurs',
      siVideMode: 'Remplacer par',
      siVideValeur: 'Une valeur fixe',
      siVideColonne: 'Une autre colonne',
    },
    transformations: {
      majuscules: 'Majuscules',
      minuscules: 'Minuscules',
      titre: 'Casse de titre',
      scinder: 'Scinder',
      extraire_nombre: 'Extraire un nombre',
      date: 'Lire une date',
      montant: 'Lire un montant',
      dictionnaire: 'Dictionnaire de valeurs',
      constante: 'Valeur fixe',
      si_vide: 'Si vide',
      concat: 'Ajouter une colonne',
      prefixe: 'Ajouter un préfixe',
    },
    transformationsAide: {
      majuscules: 'Met toutes les lettres en majuscules.',
      minuscules: 'Met toutes les lettres en minuscules.',
      titre: 'Première lettre de chaque mot en majuscule : « jean-marie » devient « Jean-Marie ».',
      scinder: 'Découpe le texte sur un séparateur et garde une partie.',
      extraire_nombre: 'Garde le premier nombre du texte : « env. 1 200 cm » donne « 1200 ».',
      date: 'Lit la date dans le format de la source et l’écrit en AAAA-MM-JJ.',
      montant: 'Lit un montant et l’écrit avec la virgule décimale.',
      dictionnaire:
        'Remplace des valeurs par d’autres. Gardez ici des valeurs générales : celles de la galerie vont dans ses choix.',
      constante: 'Donne la même valeur à toutes les lignes.',
      si_vide: 'Remplace une valeur vide par une valeur fixe ou par une autre colonne.',
      concat: 'Ajoute la valeur d’une autre colonne, avec un séparateur.',
      prefixe: 'Ajoute un texte devant la valeur.',
    },
    formatsDate: {
      'JJ/MM/AAAA': 'Jour/mois/année (03/04/2020 = 3 avril)',
      'MM/JJ/AAAA': 'Mois/jour/année (03/04/2020 = 4 mars)',
      AAAA: 'Année seule',
      excel_serie: 'Numéro de série d’un tableur',
    },
    champs: {
      aria: 'Champs du pivot',
      bilan: '{{alimentes}} champs alimentés sur {{total}}',
      obligatoires: 'Obligatoires',
      autres: 'Autres champs',
      obligatoireMarque: 'obligatoire',
      nonAlimente: 'Non alimenté',
      constante: 'Valeur fixe',
      requis: 'À alimenter',
      erreurs_one: '{{count}} erreur',
      erreurs_other: '{{count}} erreurs',
      refus_one: '{{count}} ligne refusée',
      refus_other: '{{count}} lignes refusées',
    },
    generales: {
      titre: 'Sources, jointures et lignes',
      sources: 'Fichiers lus',
      sourcesAide:
        'Un rôle par fichier. Le premier est la source principale ; le fichier de chaque rôle se choisit dans les choix de la galerie.',
      role: 'Rôle',
      feuille: 'Feuille ou tableau',
      feuilleAide: 'Vide : la première feuille remplie.',
      ajouterSource: 'Ajouter un fichier',
      retirerSource: 'Retirer le fichier {{role}}',
      jointures: 'Jointures',
      jointuresAide: 'Réunit un second fichier à la source principale par une clé commune.',
      jointureRole: 'Fichier joint',
      jointureCle: 'Clé du fichier joint',
      jointureSur: 'Clé de la source principale',
      jointureMode: 'Correspondance',
      modes: { un_a_un: 'Une ligne pour une ligne', un_a_plusieurs: 'Plusieurs lignes jointes' },
      ajouterJointure: 'Ajouter une jointure',
      retirerJointure: 'Retirer la jointure {{rang}}',
      deplier: 'Dépliage',
      deplierChemin: 'Tableau à déplier',
      deplierAide:
        'JSON ou XML : chaque élément du tableau devient une ligne, liée à son enregistrement d’origine.',
      filtres: 'Lignes gardées, pour toutes les natures',
      filtresNature: 'Lignes gardées pour cette nature',
      aucunFiltre: 'Aucun filtre : toutes les lignes sont gardées.',
      filtreColonne: 'Colonne',
      filtreOperateur: 'La ligne est gardée si la colonne',
      filtreValeur: 'Valeur',
      operateurs: {
        egal: 'est égale à',
        different: 'est différente de',
        vide: 'est vide',
        non_vide: 'n’est pas vide',
      },
      ajouterFiltre: 'Ajouter un filtre',
      retirerFiltre: 'Retirer le filtre {{rang}}',
      natureTitre: 'Réglages pour {{nature}}',
      cleOrigine: 'Clé d’origine',
      cleOrigineAide: 'La colonne qui identifie chaque ligne dans l’ancien logiciel.',
      dedoublonner: 'Dédoublonner sur',
      dedoublonnerAucun: 'Ne pas dédoublonner',
      dedoublonnerAide: 'Une seconde ligne de même valeur est écartée. Utile pour une scission.',
    },
    apercu: {
      titre: 'Aperçu sur l’échantillon',
      calcul: 'Calcul…',
      aJour: 'À jour',
      texte: 'Les règles appliquées à l’échantillon du profil. Rien n’est écrit.',
      source: '{{lus}} lignes lues, {{filtrees}} écartées par les filtres communs.',
      produites: 'Produites',
      doublons: 'Doublons',
      vides: 'Vides',
      filtrees: 'Filtrées',
      refusees: 'Refusées',
      aucuneLigne: 'Aucune ligne produite.',
      tableau: 'Lignes produites pour {{nature}}',
      reglesIncompletes: 'Les règles ne sont pas encore complètes : voir les champs signalés.',
      erreurs_one: '{{count}} erreur par ligne de la source',
      erreurs_other: '{{count}} erreurs par ligne de la source',
      avertissements_one: '{{count}} avertissement',
      avertissements_other: '{{count}} avertissements',
      ligneSource: 'Ligne {{ligne}}',
      erreursTronquees: 'Les {{count}} premières erreurs sont affichées.',
    },
    surcharges: {
      titre: 'Choix de cette galerie',
      portee: 'Cette galerie seulement',
      texte:
        'Ces réglages ne s’appliquent qu’à ce lot. Le profil reste le même pour toutes les galeries.',
      fichiers: 'Fichier de chaque rôle',
      fichiersAide: 'Sans choix, le rôle de rang n lit le fichier de rang n du lot.',
      fichierDuRole: 'Fichier du rôle « {{role}} »',
      parOrdre: 'Par ordre (fichier {{rang}})',
      dictionnaires: 'Dictionnaires de valeurs',
      dictionnairesAide:
        'Pour une valeur propre à la galerie : un lieu, un statut, un type. Ils complètent ceux du profil.',
      constantes: 'Valeurs fixes',
      constantesAide: 'Une même valeur pour toutes les lignes d’un champ. Elle remplace la source.',
      champ: 'Champ',
      ajouterDictionnaire: 'Ajouter un dictionnaire',
      ajouterConstante: 'Ajouter une valeur fixe',
      retirerDictionnaire: 'Retirer ce dictionnaire',
      retirerConstante: 'Retirer cette valeur fixe',
      enregistrer: 'Enregistrer les choix de la galerie',
      profilRequis: 'Choisissez un profil pour ce lot avant d’enregistrer ses choix.',
    },
    enregistrer: {
      titre: 'Enregistrer les règles',
      texte:
        'Un nouveau profil sert à d’autres galeries. Une nouvelle version complète un profil existant. Les lots qui citent une version la gardent.',
      nom: 'Nom du profil',
      application: 'Logiciel d’origine',
      applicationAide: 'Le logiciel dont vient l’export, pour le retrouver.',
      nouveau: 'Enregistrer comme nouveau profil',
      version: 'Nouvelle version de ce profil',
      nomRequis: 'Donnez un nom au profil pour l’enregistrer.',
      aucuneModification: 'Modifiez une règle pour publier une nouvelle version.',
      publie: 'Profil « {{nom}} » enregistré, version {{version}}.',
      lotGarde: 'Ce lot utilise toujours la version {{version}}.',
      lotSansProfil: 'Ce lot n’a pas encore de profil.',
      utiliser: 'Utiliser la version {{version}} pour ce lot',
      defait: 'Changer de version défait la transformation et annule les morceaux encore libres.',
    },
    transformation: {
      titre: 'Transformation',
      texte:
        'La transformation applique le profil à tous les fichiers du lot. Elle produit le pivot, en morceaux de 5 000 lignes.',
      utilise: 'Transformation avec la version {{version}} du profil.',
      profilRequis: 'Choisissez un profil de correspondance avant de transformer.',
      transformer: 'Transformer en pivot',
      transformerDeNouveau: 'Transformer de nouveau',
      enCours: 'Transformation en cours. La page se met à jour toute seule.',
      renonce: 'La transformation a renoncé : {{message}}',
      refaire: 'Transformer de nouveau annule les morceaux encore libres.',
      morceauxLances:
        'Un morceau est déjà lancé : le lot ne peut plus changer. Déposez un nouveau lot pour reprendre autrement.',
      rapport: 'Rapport de transformation',
      lues: 'Lignes lues',
      filtrees: 'Lignes filtrées',
      produites: 'Lignes produites',
      erreurs: 'Erreurs',
      parNature: 'Résultat par nature',
      colNature: 'Nature',
      pivot: 'Pivot',
      telechargerPivot: 'Télécharger en CSV',
      erreursTitre_one: '{{count}} erreur de lecture',
      erreursTitre_other: '{{count}} erreurs de lecture',
      erreursGardees: 'Erreurs gardées dans le rapport : {{count}} sur {{total}}.',
      rapportErreur: 'Le rapport de transformation n’a pas pu être lu.',
    },
    morceaux: {
      titre_one: '{{count}} morceau de pivot',
      titre_other: '{{count}} morceaux de pivot',
      nom: '{{nature}}, morceau {{rang}}',
      colonnes: { morceau: 'Morceau', lignes: 'Lignes', etat: 'État', action: 'Actions' },
      nonEssaye: 'Pas encore essayé',
      ouvrir: 'Ouvrir la fiche : {{nature}}, morceau {{rang}}',
      ouvrirCourt: 'Ouvrir',
      telecharger: 'Télécharger en CSV : {{nature}}, morceau {{rang}}',
      csv: 'CSV',
      scission:
        'Le pivot compte plusieurs natures, par scission : chacune a ses morceaux, à lancer dans l’ordre qui convient.',
      retourParent: 'Retour au lot d’origine',
      titreFiche: '{{nature}}, morceau {{rang}}',
      corrigerAide:
        'L’essai a refusé des lignes. Corrigez les règles dans le lot d’origine, puis transformez-le de nouveau : ses morceaux encore libres sont refaits.',
    },
    essai: {
      titre: 'Essai par morceaux',
      texte:
        'L’essai lit chaque morceau comme un lot ordinaire, sans rien écrire chez la galerie. Le rapport regroupe les refus par message.',
      essayer: 'Essayer les morceaux',
      essayerDeNouveau: 'Essayer de nouveau',
      rapport: 'Essai : {{essayes}} morceaux sur {{total}}',
      total: 'Lignes essayées',
      crees: 'Acceptées',
      sansRefus: 'Aucun refus.',
      refusParMessage: 'Refus, par message',
      occurrences_one: '{{count}} ligne',
      occurrences_other: '{{count}} lignes',
      voirLignes: 'Voir des lignes concernées',
      occurrence: '{{morceau}}, ligne {{ligne}} du pivot : {{libelle}}',
    },
    lancement: {
      titre: 'Lancement',
      texte:
        'Chaque morceau se lance depuis sa fiche, après un essai sans refus. Les morceaux se lancent un par un.',
      parent:
        'Le lot d’origine ne se lance pas : l’API le refuse (409), car ses morceaux sont les lots à lancer.',
      avancement: '{{lances}} morceaux lancés sur {{total}}',
    },
    bibliotheque: {
      titre: 'Profils de reprise',
      texte:
        'Les règles de correspondance réutilisables d’une galerie à l’autre : un profil par logiciel d’origine et par nature.',
      liste: 'Profils',
      filtreNature: 'Filtrer par nature',
      toutesNatures: 'Toutes les natures',
      aucun: 'Aucun profil pour cette nature.',
      erreur: 'La bibliothèque n’a pas pu être lue.',
      colonnes: {
        nom: 'Nom',
        application: 'Logiciel d’origine',
        nature: 'Nature',
        version: 'Version',
        action: 'Action',
      },
      fourni: 'Fourni',
      fournie: 'Fournie avec Artosera',
      consulter: 'Consulter le profil {{nom}}',
      consulterCourt: 'Consulter',
      detail: '{{nom}} · version {{version}}',
      detailChargement: 'Profil',
      lectureSeule:
        'Une version ne se modifie pas. Pour la corriger, ouvrez un lot qui l’utilise : l’éditeur publie la version suivante.',
      versions: 'Versions',
      versionNumero: 'Version {{version}}',
      signature_one: 'Signature : {{count}} en-tête attendu.',
      signature_other: 'Signature : {{count}} en-têtes attendus.',
      fermer: 'Fermer le profil',
    },
    lecture: {
      champ: 'Champ',
      source: 'Source',
      transformations: 'Transformations',
      tableau: 'Règles pour {{nature}}',
      jointure: '{{role}} : jointure sur « {{cle}} » = « {{sur}} » ({{mode}})',
      deplier: 'Tableau déplié : {{chemin}}',
      filtres: 'Lignes gardées si : {{filtres}}',
      cleOrigine: 'Clé d’origine : {{cle}}',
      dedoublonner: 'Dédoublonné sur le champ {{champ}}',
    },
    erreurs: {
      dateAmbigue:
        'La colonne « {{colonne}} » contient des dates ambiguës : choisissez le format des dates de la source.',
      formatDateManquant:
        'Un format de date manque. Ajoutez une lecture de date aux champs signalés, puis enregistrez.',
      depot_inutilisable:
        'Ce dépôt n’est pas utilisable : son inventaire n’est pas fait, ou un fichier attend l’antivirus.',
      fichier_inutilisable: 'Un fichier choisi n’est pas accepté dans ce dépôt.',
      fichier_en_double: 'Un fichier est cité deux fois.',
      format_non_pris_en_charge: 'Un fichier n’est ni un tableur, ni du JSON, du XML ou une vCard.',
      sources_trop_lourdes:
        'Les fichiers d’un lot dépassent 200 Mo : répartissez-les en plusieurs lots.',
      lot_sans_depot: 'Seul un lot tiré d’un dépôt se profile et se transforme.',
      travail_en_cours: 'Un travail de ce lot est déjà en cours.',
      profil_requis: 'Choisissez un profil de correspondance avant de continuer.',
      profil_hors_nature: 'Ce profil ne correspond pas à la nature du lot.',
      morceaux_lances:
        'Un morceau de ce lot est déjà lancé : déposez un nouveau lot pour reprendre autrement.',
      lot_non_profile: 'Ce lot n’est pas encore profilé.',
      lot_non_transforme: 'Transformez le lot en pivot avant de l’essayer.',
      lot_de_correspondance:
        'Ce lot vient d’un dépôt : ses morceaux se lancent un par un, depuis leur fiche.',
      rien_a_essayer: 'Aucun morceau n’est à essayer.',
      role_sans_fichier:
        'Un fichier manque pour un rôle de source : choisissez-le dans les choix de la galerie.',
      table_sans_echantillon: 'La feuille ou le tableau demandé n’existe pas dans le fichier.',
      version_perimee:
        'Une version plus récente de ce profil a été publiée. Passez ce lot à cette version, puis refaites vos modifications.',
      regles_invalides: 'Les règles ne sont pas valides. Les champs concernés sont signalés.',
      ambigus_a_trancher:
        'Des fichiers sont ambigus : rattachez-les à une œuvre ou ignorez-les, puis rapprochez de nouveau. Aucun essai n’est possible avant.',
      lot_sans_images: 'Ce lot n’est pas un lot d’images tiré d’un dépôt.',
    },
  },
};

export const enCorrespondance: typeof frCorrespondance = {
  correspondance: {
    etapes: {
      aria: 'Batch progress',
      depose: 'Uploaded',
      profile: 'Profiled',
      correspondance: 'Mapping',
      transforme: 'Transformed',
      essai: 'Trial by chunk',
      a_lancer: 'Chunks to launch',
      etat: { fait: 'Done', courante: 'To do now', a_venir: 'Coming up' },
      detailEssai: '{{faits}} of {{total}} tried',
      detailLancement: '{{faits}} of {{total}} launched',
    },
    liste: {
      depuisDepotBouton: 'Batch from an upload',
      depuisDepot: 'From an upload, to profile',
      morceaux_one: '{{count}} chunk · ready: {{prets}}',
      morceaux_other: '{{count}} chunks · ready: {{prets}}',
    },
    nouveau: {
      titre: 'New batch from an upload',
      texte:
        'Pick a ready upload, then the files to read. Nothing is written to the gallery before the trial.',
      depot: 'Upload',
      depotChoix: 'Upload to use',
      depotAucunChoix: 'Choose an upload',
      depotAucun: 'No upload is ready. An upload becomes usable once its inventory is done.',
      fichiers: 'Files',
      illisible: 'Unsupported format',
      choisis_one: '{{count}} file chosen, {{max}} at most.',
      choisis_other: '{{count}} files chosen, {{max}} at most.',
      nature: 'Main kind of the batch',
      natureAide:
        'The kind decides the profile and the fields to fill. Other kinds come from splitting, in the editor.',
      profilage: 'Profiling',
      profilageTexte:
        'Profiling reads the files and describes their columns: formats, fill rate, ambiguous dates. It changes nothing.',
      profiler: 'Start profiling',
      profilageRefuse:
        'The batch is created, but profiling was refused: {{message}} Open the batch to retry.',
      ouvrir: 'Open the batch',
      annuler: 'Cancel',
      ok: 'Batch created, profiling started.',
    },
    fiche: {
      titre: '{{nature}} · batch from an upload',
      profiler: 'Start profiling',
      reprofiler: 'Profile again',
      profilerAide: 'Profiling reads the batch files and describes their columns.',
      reprofilerAide: 'Profiling again rereads the files. Launched chunks forbid it.',
      profilageEnCours: 'Profiling in progress. The page updates by itself.',
      renonce: 'The job gave up: {{message}}',
    },
    profilCite: {
      aucun: 'No mapping profile chosen for this batch.',
      utilise: 'Profile in use: {{nom}}',
      nomInconnu: 'profile not found',
      version: 'version {{version}}',
      plusRecente:
        'Version {{version}} of this profile exists. This batch keeps version {{actuelle}} until you change it.',
      passer: 'Switch to version {{version}}',
    },
    profil: {
      titre: 'Source profile',
      texte:
        'What profiling read: each file’s format, then each column’s fill rate, frequent values and format.',
      erreur: 'The source profile could not be read.',
      tableNommee: 'Sheet “{{nom}}” · {{count}} rows',
      tableUnique: '{{count}} rows',
      colonne: 'Column',
      remplissage: 'Fill rate',
      remplissageColonne: 'Fill rate of column {{nom}}',
      frequentes: 'Frequent values',
      format: 'Detected format',
      aucuneValeur: 'No value',
      autresValeurs_one: '{{count}} other value',
      autresValeurs_other: '{{count}} other values',
      encodage: '{{valeur}} encoding',
      separateur: '“{{valeur}}” separator',
      illisible: 'Unreadable file: {{message}}',
      illisiblesTitre_one: '{{count}} file could not be read.',
      illisiblesTitre_other: '{{count}} files could not be read.',
      ambiguesTitre_one: '{{count}} date column is ambiguous.',
      ambiguesTitre_other: '{{count}} date columns are ambiguous.',
      ambigueLigne: '“{{colonne}}” in {{fichier}} (for example {{exemple}}).',
      ambiguesRegle:
        'Day and month can swap. The rules must declare these dates’ format, or saving is blocked.',
      ambigueAide: 'Day and month can swap (03/04: 3 April or 4 March?).',
      formats: {
        vide: 'Empty',
        date_iso: 'Date (YYYY-MM-DD)',
        date_fr: 'Date (DD/MM/YYYY)',
        date_us: 'Date (MM/DD/YYYY)',
        date_ambigue: 'Ambiguous date',
        annee: 'Year',
        excel_serie: 'Date as serial number',
        courriel: 'Email',
        url: 'Web address',
        devise: 'Currency',
        montant: 'Amount',
        nombre: 'Number',
        liste: 'List',
        texte: 'Text',
      },
    },
    suggestions: {
      titre: 'Suggested profiles',
      aucune: 'No known profile resembles these headers. Write the rules below.',
      score: '{{score}}%',
      detail: 'Version {{version}} · {{trouves}} of {{attendus}} columns found',
      appliquer: 'Apply as a base',
      utilise: 'Used by this batch',
      defait: 'Changing profile undoes the transformation and cancels the chunks still free.',
    },
    editeur: {
      titre: 'Mapping',
      texte:
        'Say where each field of the pivot comes from. The preview updates with every change, on the profile’s sample.',
      droitProfils: 'Writing rules requires the right to manage migration profiles.',
      erreurChargement: 'The editor could not load the profile or the pivot fields.',
      partDe: 'Rules of profile {{nom}}, version {{version}}',
      partDeRien: 'Rules to write: no profile chosen',
      modifie: 'Edited',
      natures: 'Kinds being filled',
      ajouterNature: 'Add a kind',
      retirerNature: 'Remove kind {{nature}}',
      retourChamps: 'Back to fields',
      choisirChamp: 'Choose a field in the list.',
      obligatoire: 'Required',
      facultatif: 'Optional',
      origine: 'Where the field comes from',
      modes: {
        colonne: 'One column',
        plusieurs: 'Several columns',
        chemin: 'JSON or XML path',
      },
      colonne: 'Source column',
      colonneNumero: 'Column {{rang}}',
      colonneAjoutee: 'Column to add',
      chemin: 'Path in the file',
      ajouterColonne: 'Add a column',
      retirerColonne: 'Remove column {{rang}}',
      separateurColonnes: 'Separator',
      exemples: 'Examples in the source: {{valeurs}}.',
      role: 'File',
      rolePrincipal: 'Main source',
      parent: 'Read from the parent record',
      parentCourt: 'parent',
      dateAmbigueTitre: 'Column “{{colonne}}” contains ambiguous dates.',
      dateAmbigueTexte: 'Choose the format of the source dates: day and month can swap.',
      ajouterDate: 'Add the date reading',
      transformations: 'Transformations',
      aucuneTransformation: 'No transformation: the source value is kept as it is.',
      etapeNumero: 'Step {{rang}}: {{nom}}',
      typeEtape: 'Transformation',
      monter: 'Move step {{nom}} up',
      descendre: 'Move step {{nom}} down',
      monterCourt: 'Up',
      descendreCourt: 'Down',
      retirerEtape: 'Remove step {{nom}}',
      retirer: 'Remove',
      nouvelleEtape: 'New transformation',
      ajouterEtape: 'Add the step',
      viderChamp: 'Clear this field',
      separateur: 'Separator',
      indexScinder: 'Part number',
      indexAide: '0 for the first, -1 for the last.',
      formatDate: 'Format of the source dates',
      decimal: 'Decimal separator',
      decimalCourt: 'decimal',
      espaceCourt: 'space',
      decimalVirgule: 'Comma (1 200,50)',
      decimalPoint: 'Point (1,200.50)',
      devise: 'Currency',
      deviseAucune: 'No currency',
      dictionnaire: 'Values, one per line',
      dictionnaireAide: 'Write “source value = kept value”. Case and accents are ignored.',
      valeurParDefaut: 'Default value',
      valeur: 'Value',
      valeurs_one: '{{count}} value',
      valeurs_other: '{{count}} values',
      siVideMode: 'Replace with',
      siVideValeur: 'A fixed value',
      siVideColonne: 'Another column',
    },
    transformations: {
      majuscules: 'Uppercase',
      minuscules: 'Lowercase',
      titre: 'Title case',
      scinder: 'Split',
      extraire_nombre: 'Extract a number',
      date: 'Read a date',
      montant: 'Read an amount',
      dictionnaire: 'Value dictionary',
      constante: 'Fixed value',
      si_vide: 'If empty',
      concat: 'Add a column',
      prefixe: 'Add a prefix',
    },
    transformationsAide: {
      majuscules: 'Turns every letter into uppercase.',
      minuscules: 'Turns every letter into lowercase.',
      titre: 'First letter of each word in uppercase: “jean-marie” becomes “Jean-Marie”.',
      scinder: 'Cuts the text on a separator and keeps one part.',
      extraire_nombre: 'Keeps the first number in the text: “approx. 1 200 cm” gives “1200”.',
      date: 'Reads the date in the source format and writes it as YYYY-MM-DD.',
      montant: 'Reads an amount and writes it with a decimal comma.',
      dictionnaire:
        'Replaces values with others. Keep general values here: the gallery’s own go in its choices.',
      constante: 'Gives every row the same value.',
      si_vide: 'Replaces an empty value with a fixed value or another column.',
      concat: 'Appends another column’s value, with a separator.',
      prefixe: 'Adds text in front of the value.',
    },
    formatsDate: {
      'JJ/MM/AAAA': 'Day/month/year (03/04/2020 = 3 April)',
      'MM/JJ/AAAA': 'Month/day/year (03/04/2020 = 4 March)',
      AAAA: 'Year only',
      excel_serie: 'Spreadsheet serial number',
    },
    champs: {
      aria: 'Pivot fields',
      bilan: '{{alimentes}} of {{total}} fields filled',
      obligatoires: 'Required',
      autres: 'Other fields',
      obligatoireMarque: 'required',
      nonAlimente: 'Not filled',
      constante: 'Fixed value',
      requis: 'To fill',
      erreurs_one: '{{count}} error',
      erreurs_other: '{{count}} errors',
      refus_one: '{{count}} row refused',
      refus_other: '{{count}} rows refused',
    },
    generales: {
      titre: 'Sources, joins and rows',
      sources: 'Files read',
      sourcesAide:
        'One role per file. The first is the main source; each role’s file is chosen in the gallery’s choices.',
      role: 'Role',
      feuille: 'Sheet or array',
      feuilleAide: 'Empty: the first filled sheet.',
      ajouterSource: 'Add a file',
      retirerSource: 'Remove file {{role}}',
      jointures: 'Joins',
      jointuresAide: 'Joins a second file to the main source through a shared key.',
      jointureRole: 'Joined file',
      jointureCle: 'Key of the joined file',
      jointureSur: 'Key of the main source',
      jointureMode: 'Matching',
      modes: { un_a_un: 'One row for one row', un_a_plusieurs: 'Several joined rows' },
      ajouterJointure: 'Add a join',
      retirerJointure: 'Remove join {{rang}}',
      deplier: 'Unfolding',
      deplierChemin: 'Array to unfold',
      deplierAide: 'JSON or XML: each array element becomes a row, linked to its original record.',
      filtres: 'Rows kept, for every kind',
      filtresNature: 'Rows kept for this kind',
      aucunFiltre: 'No filter: every row is kept.',
      filtreColonne: 'Column',
      filtreOperateur: 'The row is kept if the column',
      filtreValeur: 'Value',
      operateurs: {
        egal: 'equals',
        different: 'differs from',
        vide: 'is empty',
        non_vide: 'is not empty',
      },
      ajouterFiltre: 'Add a filter',
      retirerFiltre: 'Remove filter {{rang}}',
      natureTitre: 'Settings for {{nature}}',
      cleOrigine: 'Origin key',
      cleOrigineAide: 'The column that identifies each row in the former software.',
      dedoublonner: 'Remove duplicates on',
      dedoublonnerAucun: 'Keep duplicates',
      dedoublonnerAide: 'A second row with the same value is dropped. Useful for splitting.',
    },
    apercu: {
      titre: 'Preview on the sample',
      calcul: 'Computing…',
      aJour: 'Up to date',
      texte: 'The rules applied to the profile’s sample. Nothing is written.',
      source: '{{lus}} rows read, {{filtrees}} dropped by the common filters.',
      produites: 'Produced',
      doublons: 'Duplicates',
      vides: 'Empty',
      filtrees: 'Filtered',
      refusees: 'Refused',
      aucuneLigne: 'No row produced.',
      tableau: 'Rows produced for {{nature}}',
      reglesIncompletes: 'The rules are not complete yet: see the flagged fields.',
      erreurs_one: '{{count}} error by source row',
      erreurs_other: '{{count}} errors by source row',
      avertissements_one: '{{count}} warning',
      avertissements_other: '{{count}} warnings',
      ligneSource: 'Row {{ligne}}',
      erreursTronquees: 'The first {{count}} errors are shown.',
    },
    surcharges: {
      titre: 'This gallery’s choices',
      portee: 'This gallery only',
      texte:
        'These settings apply to this batch only. The profile stays the same for every gallery.',
      fichiers: 'File of each role',
      fichiersAide: 'Without a choice, the role of rank n reads the batch file of rank n.',
      fichierDuRole: 'File of role “{{role}}”',
      parOrdre: 'By order (file {{rang}})',
      dictionnaires: 'Value dictionaries',
      dictionnairesAide:
        'For a value specific to the gallery: a place, a status, a type. They add to the profile’s.',
      constantes: 'Fixed values',
      constantesAide: 'The same value for every row of a field. It replaces the source.',
      champ: 'Field',
      ajouterDictionnaire: 'Add a dictionary',
      ajouterConstante: 'Add a fixed value',
      retirerDictionnaire: 'Remove this dictionary',
      retirerConstante: 'Remove this fixed value',
      enregistrer: 'Save the gallery’s choices',
      profilRequis: 'Choose a profile for this batch before saving its choices.',
    },
    enregistrer: {
      titre: 'Save the rules',
      texte:
        'A new profile serves other galleries. A new version completes an existing profile. Batches that cite a version keep it.',
      nom: 'Profile name',
      application: 'Former software',
      applicationAide: 'The software the export comes from, to find it again.',
      nouveau: 'Save as a new profile',
      version: 'New version of this profile',
      nomRequis: 'Name the profile to save it.',
      aucuneModification: 'Edit a rule to publish a new version.',
      publie: 'Profile “{{nom}}” saved, version {{version}}.',
      lotGarde: 'This batch still uses version {{version}}.',
      lotSansProfil: 'This batch has no profile yet.',
      utiliser: 'Use version {{version}} for this batch',
      defait: 'Changing version undoes the transformation and cancels the chunks still free.',
    },
    transformation: {
      titre: 'Transformation',
      texte:
        'The transformation applies the profile to all the batch files. It produces the pivot, in chunks of 5,000 rows.',
      utilise: 'Transformation with version {{version}} of the profile.',
      profilRequis: 'Choose a mapping profile before transforming.',
      transformer: 'Transform into pivot',
      transformerDeNouveau: 'Transform again',
      enCours: 'Transformation in progress. The page updates by itself.',
      renonce: 'The transformation gave up: {{message}}',
      refaire: 'Transforming again cancels the chunks still free.',
      morceauxLances:
        'A chunk is already launched: the batch can no longer change. Upload a new batch to start over.',
      rapport: 'Transformation report',
      lues: 'Rows read',
      filtrees: 'Rows filtered',
      produites: 'Rows produced',
      erreurs: 'Errors',
      parNature: 'Result by kind',
      colNature: 'Kind',
      pivot: 'Pivot',
      telechargerPivot: 'Download as CSV',
      erreursTitre_one: '{{count}} reading error',
      erreursTitre_other: '{{count}} reading errors',
      erreursGardees: 'Errors kept in the report: {{count}} of {{total}}.',
      rapportErreur: 'The transformation report could not be read.',
    },
    morceaux: {
      titre_one: '{{count}} pivot chunk',
      titre_other: '{{count}} pivot chunks',
      nom: '{{nature}}, chunk {{rang}}',
      colonnes: { morceau: 'Chunk', lignes: 'Rows', etat: 'Status', action: 'Actions' },
      nonEssaye: 'Not tried yet',
      ouvrir: 'Open the page: {{nature}}, chunk {{rang}}',
      ouvrirCourt: 'Open',
      telecharger: 'Download as CSV: {{nature}}, chunk {{rang}}',
      csv: 'CSV',
      scission:
        'The pivot holds several kinds, through splitting: each has its chunks, to launch in the order that suits.',
      retourParent: 'Back to the original batch',
      titreFiche: '{{nature}}, chunk {{rang}}',
      corrigerAide:
        'The trial refused some rows. Fix the rules in the original batch, then transform it again: its chunks still free are redone.',
    },
    essai: {
      titre: 'Trial by chunk',
      texte:
        'The trial reads each chunk like an ordinary batch, writing nothing to the gallery. The report groups refusals by message.',
      essayer: 'Try the chunks',
      essayerDeNouveau: 'Try again',
      rapport: 'Trial: {{essayes}} of {{total}} chunks',
      total: 'Rows tried',
      crees: 'Accepted',
      sansRefus: 'No refusal.',
      refusParMessage: 'Refusals, by message',
      occurrences_one: '{{count}} row',
      occurrences_other: '{{count}} rows',
      voirLignes: 'See some of the rows',
      occurrence: '{{morceau}}, pivot row {{ligne}}: {{libelle}}',
    },
    lancement: {
      titre: 'Launch',
      texte:
        'Each chunk launches from its own page, after a trial with no refusal. Chunks launch one by one.',
      parent:
        'The original batch does not launch: the API refuses it (409), because its chunks are the batches to launch.',
      avancement: '{{lances}} of {{total}} chunks launched',
    },
    bibliotheque: {
      titre: 'Migration profiles',
      texte:
        'Mapping rules reusable from one gallery to another: one profile per former software and per kind.',
      liste: 'Profiles',
      filtreNature: 'Filter by kind',
      toutesNatures: 'All kinds',
      aucun: 'No profile for this kind.',
      erreur: 'The library could not be read.',
      colonnes: {
        nom: 'Name',
        application: 'Former software',
        nature: 'Kind',
        version: 'Version',
        action: 'Action',
      },
      fourni: 'Provided',
      fournie: 'Provided with Artosera',
      consulter: 'View profile {{nom}}',
      consulterCourt: 'View',
      detail: '{{nom}} · version {{version}}',
      detailChargement: 'Profile',
      lectureSeule:
        'A version cannot be edited. To fix it, open a batch that uses it: the editor publishes the next version.',
      versions: 'Versions',
      versionNumero: 'Version {{version}}',
      signature_one: 'Signature: {{count}} expected header.',
      signature_other: 'Signature: {{count}} expected headers.',
      fermer: 'Close the profile',
    },
    lecture: {
      champ: 'Field',
      source: 'Source',
      transformations: 'Transformations',
      tableau: 'Rules for {{nature}}',
      jointure: '{{role}}: join on “{{cle}}” = “{{sur}}” ({{mode}})',
      deplier: 'Unfolded array: {{chemin}}',
      filtres: 'Rows kept if: {{filtres}}',
      cleOrigine: 'Origin key: {{cle}}',
      dedoublonner: 'Duplicates removed on field {{champ}}',
    },
    erreurs: {
      dateAmbigue:
        'Column “{{colonne}}” contains ambiguous dates: choose the format of the source dates.',
      formatDateManquant:
        'A date format is missing. Add a date reading to the flagged fields, then save.',
      depot_inutilisable:
        'This upload is not usable: its inventory is not done, or a file is waiting for the antivirus.',
      fichier_inutilisable: 'A chosen file is not accepted in this upload.',
      fichier_en_double: 'A file is cited twice.',
      format_non_pris_en_charge: 'A file is neither a spreadsheet, nor JSON, XML or a vCard.',
      sources_trop_lourdes: 'A batch’s files exceed 200 MB: split them into several batches.',
      lot_sans_depot: 'Only a batch taken from an upload is profiled and transformed.',
      travail_en_cours: 'A job of this batch is already running.',
      profil_requis: 'Choose a mapping profile before going on.',
      profil_hors_nature: 'This profile does not match the batch’s kind.',
      morceaux_lances:
        'A chunk of this batch is already launched: upload a new batch to start over.',
      lot_non_profile: 'This batch is not profiled yet.',
      lot_non_transforme: 'Transform the batch into pivot before trying it.',
      lot_de_correspondance:
        'This batch comes from an upload: its chunks launch one by one, from their own page.',
      rien_a_essayer: 'No chunk is left to try.',
      role_sans_fichier: 'A file is missing for a source role: choose it in the gallery’s choices.',
      table_sans_echantillon: 'The requested sheet or array does not exist in the file.',
      version_perimee:
        'A newer version of this profile was published. Switch this batch to it, then redo your edits.',
      regles_invalides: 'The rules are not valid. The fields concerned are flagged.',
      ambigus_a_trancher:
        'Some files are ambiguous: attach them to an artwork or ignore them, then match again. No trial is possible before.',
      lot_sans_images: 'This batch is not an image batch taken from an upload.',
    },
  },
};
